import {
  Module,
  VuexModule,
  Action,
  Mutation,
  getModule,
} from 'vuex-module-decorators';
import store from '@/store/swine';
import { axios } from '@/utils/axios';
import { Endpoints } from '@/utils/endpoints';
import { ToastProgrammatic as Toast } from 'buefy';
import {
  EditClient,
  DropdownOptionRequest,
  DropdownOptionResponse,
} from '@/models/swine/client/client.model';
import { Farm } from '@/models/swine/farm/farm.model';
import { DEFAULT_SELECT_OPTION } from '@/utils/constants';
import { DropdownOption } from '@/models/swine/dropdown.model';
import { Region } from '@/models/swine/regions/region.model';
import { State } from '@/models/swine/states/state.model';
import { City } from '@/models/swine/cities/city.model';
import { generateUniqueId } from '@/utils/lodash';

@Module({
  namespaced: true,
  dynamic: true,
  name: 'client.detail',
  store,
})
class ClientDetail extends VuexModule {
  response: any | null = null;
  isLoading = false;
  error: any | null = null;
  clientDetails: EditClient = {
    id: 1,
    name: '',
    species: '',
    addressLine1: '',
    countryId: '',
    regionId: '',
    cityId: '',
    stateId: '',
    postcode: '',
    phoneNumber: '',
    clientCode: '',
    farms: [],
    country: {
      id: 1,
      code: '',
      name: '',
    },
    region: {
      id: 1,
      code: '',
      name: '',
    },
    state: {
      id: 1,
      code: '',
      name: '',
    },
    city: {
      id: 1,
      name: '',
    },
  };
  regionsList: DropdownOptionResponse | null = null;
  statesList: DropdownOptionResponse | null = null;
  citiesList: DropdownOptionResponse | null = null;
  isEditPage : boolean = false;

  @Mutation
  updateLoadingStatus(payload: boolean) {
    this.isLoading = payload;
  }

  @Mutation
  setError(error: any) {
    this.error = error;
  }

  @Mutation
  setResponse(payload: any) {
    this.response = payload;
  }

  @Mutation
  setClientDetails(payload: EditClient) {
    this.clientDetails = payload;
  }

  @Mutation
  updateName(payload: string) {
    this.clientDetails.name = payload;
  }

  @Mutation
  updateCountry(payload: string) {
    this.clientDetails.countryId = payload;
  }

  @Mutation
  updateRegion(payload: string) {
    this.clientDetails.regionId = payload;
  }

  @Mutation
  updateState(payload: string) {
    this.clientDetails.stateId = payload;
  }

  @Mutation
  updateCity(payload: string) {
    this.clientDetails.cityId = payload;
  }

  @Mutation
  updateStreetAddress(payload: string) {
    this.clientDetails.addressLine1 = payload;
  }

  @Mutation
  updatePhone(payload: string) {
    this.clientDetails.phoneNumber = payload;
  }

  @Mutation
  updatePostcode(payload: string) {
    this.clientDetails.postcode = payload;
  }

  @Mutation
  updateClientCode(payload: string) {
    this.clientDetails.clientCode = payload;
  }

  @Mutation
  updateFarm(payload: Farm[]) {
    this.clientDetails.farms = payload;
  }

  @Mutation
  setRegionsList(payload: DropdownOptionResponse) {
    this.regionsList = payload;
  }

  @Mutation
  setStatesList(payload: DropdownOptionResponse) {
    this.statesList = payload;
  }

  @Mutation
  setCitiesList(payload: DropdownOptionResponse) {
    this.citiesList = payload;
  }

  @Mutation
  setSpecies(payload: string) {
    this.clientDetails.species = payload;
  }

  @Mutation
  setEditPage(payload: boolean) {
    this.isEditPage = payload;
  }

  @Mutation
  resetClientDetails() {
    this.clientDetails = {
      id: 1,
      name: '',
      addressLine1: '',
      countryId: '',
      regionId: '',
      stateId: '',
      cityId: '',
      postcode: '',
      phoneNumber: '',
      clientCode: '',
      farms: [],
      country: {
        id: 1,
        code: '',
        name: '',
      },
      region: {
        id: 1,
        code: '',
        name: '',
      },
      state: {
        id: 1,
        code: '',
        name: '',
      },
      city: {
        id: 1,
        name: '',
      },
    };
    this.isEditPage = false;
  }

  @Action
  async getClientDetails(clientId: string) {
    this.context.commit('updateLoadingStatus', true);
    this.context.commit('setError', null);
    try {
      const response = await axios.get(`${Endpoints.Clients}/${clientId}`, {
        params: {
          species: this.clientDetails.species
        },
      },);
      if (response) {
        this.context.commit('setClientDetails', response.data);
      }
    } catch (error) {
      this.context.commit('setError', error);
    }
    this.context.commit('updateLoadingStatus', false);
  }

  @Action
  getClientCode() {
    this.context.commit('updateLoadingStatus', true);
    this.context.commit('setError', null);
    //if (this.clientDetails.countryId && this.clientDetails.regionId && this.clientDetails.stateId) {
    try {
      axios
        .get<string>(`${Endpoints.Clients}/client_code`, {
          params: {
            country: this.clientDetails.countryId,
            region: this.clientDetails.regionId,
            state: this.clientDetails.stateId,
            species: this.clientDetails.species,
          },
        })
        .then((response) => {
          if (response.data) {
            this.context.commit('updateClientCode', response.data);
          }
        });
    } catch (error) {
      this.context.commit('setError', error);
    }
    //}
    this.context.commit('updateLoadingStatus', false);
  }

  @Action
  async updateClient() {
    this.context.commit('updateLoadingStatus', true);
    this.context.commit('setError', null);
    try {
      axios
        .put(
          `${Endpoints.Clients}/${this.clientDetails.id}`,
          this.clientDetails,
        )
        .then((response) => this.context.commit('setResponse', response));
    } catch (error) {
      this.context.commit('setError', error);
    }
    this.context.commit('updateLoadingStatus', false);
  }

  @Action
  async deleteClient() {
    this.context.commit('updateLoadingStatus', true);
    this.context.commit('setError', null);
    try {
      axios
        .delete(`${Endpoints.Clients}/${this.clientDetails.id}`)
        .then((response) => {
          this.context.commit('setResponse', response);
          if (response.status === 200) {
            // Show toast
            Toast.open({
              type: 'is-dark',
              message: `${this.clientDetails.clientCode} has been deleted`,
              duration: 3000, // 3 seconds
            });
          }
        });
    } catch (error) {
      this.context.commit('setError', error);
    }
    this.context.commit('updateLoadingStatus', false);
  }

  @Action
  getRegions(request: DropdownOptionRequest) {
    this.context.commit('updateLoadingStatus', true);
    this.context.commit('setError', null);
    try {
      axios
        .get(Endpoints.Regions, {
          params: {
            country: request.value,
          },
        })
        .then((response) => {
          if (response.data) {
            const parsedData: DropdownOption[] = [];

            response.data.map((item: Region) => {
              parsedData.push({
                text: item.name,
                value: String(item.id),
                uniqueKey: generateUniqueId(`${item.id}_`),
              });
            });

            this.context.commit('setRegionsList', {
              code: request.code,
              list: [...DEFAULT_SELECT_OPTION, ...parsedData],
            });
          }
        });
    } catch (error) {
      this.context.commit('setError', error);
    }
    this.context.commit('updateLoadingStatus', false);
  }

  @Action
  getStates(request: DropdownOptionRequest) {
    this.context.commit('updateLoadingStatus', true);
    this.context.commit('setError', null);
    try {
      axios
        .get(Endpoints.States, {
          params: {
            region: request.value,
          },
        })
        .then((response) => {
          if (response.data) {
            const parsedData: DropdownOption[] = [];

            response.data.map((item: State) => {
              parsedData.push({
                text: item.name,
                value: String(item.id),
                uniqueKey: generateUniqueId(`${item.name}_`),
              });
            });
            this.context.commit('setStatesList', {
              code: request.code,
              list: [...DEFAULT_SELECT_OPTION, ...parsedData],
            });
          }
        });
    } catch (error) {
      this.context.commit('setError', error);
    }
    this.context.commit('updateLoadingStatus', false);
  }

  @Action
  getCities(request: DropdownOptionRequest) {
    this.context.commit('updateLoadingStatus', true);
    this.context.commit('setError', null);
    try {
      axios
        .get(Endpoints.Cities, {
          params: {
            state: request.value,
          },
        })
        .then((response) => {
          if (response.data) {
            const parsedData: DropdownOption[] = [];

            response.data.map((item: City) => {
              parsedData.push({
                text: item.name,
                value: String(item.id),
                uniqueKey: generateUniqueId(`${item.name}_`),
              });
            });

            this.context.commit('setCitiesList', {
              code: request.code,
              list: [...DEFAULT_SELECT_OPTION, ...parsedData],
            });
          }
        });
    } catch (error) {
      this.context.commit('setError', error);
    }
    this.context.commit('updateLoadingStatus', false);
  }

  @Action
  resetStore() {
    this.context.commit('updateLoadingStatus', true);
    this.context.commit('setError', null);
    try {
      this.resetClientDetails();
    } catch (error) {
      this.context.commit('setError', error);
    }
    this.context.commit('updateLoadingStatus', false);
  }
}

export default getModule(ClientDetail);
